import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Calculator, ArrowRight, Shield, Info } from "lucide-react";

const TAGLI = [25, 50, 75, 100, 150, 200, 250, 300, 400, 500];

const TABELLA = {
  "20-24": [29700, 59400, 89100, 118800, 178200, 237600, 297000, null, null, null],
  "25-29": [26250, 52500, 78750, 105000, 157500, 210000, 262500, null, null, null],
  "30-34": [20225, 40450, 60675, 80900, 121350, 161800, 202250, 242700, null, null],
  "35-39": [13800, 27600, 41400, 55200, 82800, 110400, 138000, 165600, 220800, 276000],
  "40-44": [8900, 17800, 26700, 35600, 53400, 71200, 89000, 106800, 142400, 178000],
  "45-49": [5550, 11100, 16650, 22200, 33300, 44400, 55500, 66600, 88800, 111000],
  "50-54": [null, 6750, 10125, 13500, 20250, 27000, 33750, 40500, 54000, 67500],
  "55-59": [null, null, 6225, 8300, 12450, 16600, 20750, 24900, 33200, 41500],
};

const FASCE = ["20-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54", "55-59"];

function getFascia(eta) {
  if (eta >= 20 && eta <= 24) return "20-24";
  if (eta >= 25 && eta <= 29) return "25-29";
  if (eta >= 30 && eta <= 34) return "30-34";
  if (eta >= 35 && eta <= 39) return "35-39";
  if (eta >= 40 && eta <= 44) return "40-44";
  if (eta >= 45 && eta <= 49) return "45-49";
  if (eta >= 50 && eta <= 54) return "50-54";
  if (eta >= 55 && eta <= 59) return "55-59";
  return null;
}

function fmt(val) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

export default function QuotatorePage() {
  const navigate = useNavigate();
  const [eta, setEta] = useState(35);
  const [capitaleIdx, setCapitaleIdx] = useState(3);

  const fascia = getFascia(eta);
  const capitaliDisponibili = useMemo(() => {
    if (!fascia) return [];
    const row = TABELLA[fascia];
    return row.map((val, i) => ({ taglio: TAGLI[i], capitale: val, idx: i })).filter(x => x.capitale !== null);
  }, [fascia]);

  const clampedIdx = Math.min(capitaleIdx, capitaliDisponibili.length - 1);
  const current = capitaliDisponibili[clampedIdx] || null;

  const etaPercent = ((eta - 20) / (59 - 20)) * 100;
  const capPercent = capitaliDisponibili.length > 1 ? (clampedIdx / (capitaliDisponibili.length - 1)) * 100 : 50;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="quotatore-page">
        <div className="max-w-xl mx-auto px-5 sm:px-6 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Calculator className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="quotatore-title">
              Quotatore Vita
            </h1>
            <p className="text-sm text-[#4A5E51]">Scopri il capitale assicurato in base alla tua et&agrave; e al premio scelto</p>
            <p className="text-[10px] text-[#4A5E51] mt-1">Capitale e premi indicativi &mdash; quotazione finale a cura del consulente</p>
          </div>

          {/* Risultato centrale */}
          <div className="mb-10">
            {current ? (
              <div className="p-8 sm:p-10 bg-gradient-to-br from-[#163321] to-[#224A32] rounded-3xl text-white text-center space-y-2 shadow-lg" data-testid="quotatore-risultato">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Premio annuo</p>
                <p className="text-2xl sm:text-3xl font-heading font-medium text-[#D97B58]" data-testid="quotatore-premio">
                  {fmt(current.taglio)}<span className="text-base font-normal text-white/50">/anno</span>
                </p>
                <div className="w-16 h-px bg-white/20 mx-auto my-3" />
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Capitale assicurato</p>
                <p className="text-4xl sm:text-5xl font-heading font-medium" data-testid="quotatore-capitale">
                  {fmt(current.capitale)}
                </p>
                <p className="text-xs text-white/40 pt-2">Et&agrave; {eta} anni &middot; Fascia {fascia}</p>
              </div>
            ) : (
              <div className="p-8 bg-[#F5F4F0] rounded-3xl text-center">
                <p className="text-sm text-[#4A5E51]">Seleziona un'et&agrave; valida (20-59 anni)</p>
              </div>
            )}
          </div>

          {/* Slider Età */}
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm space-y-8" data-testid="quotatore-sliders">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-[#0F1F14]">Et&agrave;</label>
                <span className="text-lg font-heading font-medium text-[#163321] bg-[#163321]/5 px-3 py-1 rounded-lg" data-testid="quotatore-eta-value">{eta} anni</span>
              </div>
              <div className="relative pt-2 pb-1">
                <div className="absolute top-1/2 left-0 right-0 h-2 bg-[#E5E2DB] rounded-full -translate-y-1/2" />
                <div className="absolute top-1/2 left-0 h-2 bg-[#163321] rounded-full -translate-y-1/2" style={{ width: `${etaPercent}%` }} />
                <input
                  type="range"
                  min="20"
                  max="59"
                  value={eta}
                  onChange={e => setEta(parseInt(e.target.value))}
                  className="relative w-full h-2 appearance-none bg-transparent cursor-pointer z-10 slider-thumb"
                  data-testid="quotatore-eta-slider"
                />
              </div>
              <div className="flex justify-between text-[10px] text-[#4A5E51]">
                <span>20 anni</span>
                <span>59 anni</span>
              </div>
            </div>

            {/* Slider Capitale */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-[#0F1F14]">Capitale</label>
                {current && (
                  <span className="text-lg font-heading font-medium text-[#D97B58] bg-[#D97B58]/10 px-3 py-1 rounded-lg" data-testid="quotatore-cap-value">{fmt(current.capitale)}</span>
                )}
              </div>
              <div className="relative pt-2 pb-1">
                <div className="absolute top-1/2 left-0 right-0 h-2 bg-[#E5E2DB] rounded-full -translate-y-1/2" />
                <div className="absolute top-1/2 left-0 h-2 bg-[#D97B58] rounded-full -translate-y-1/2" style={{ width: `${capPercent}%` }} />
                <input
                  type="range"
                  min="0"
                  max={Math.max(capitaliDisponibili.length - 1, 0)}
                  value={clampedIdx}
                  onChange={e => setCapitaleIdx(parseInt(e.target.value))}
                  className="relative w-full h-2 appearance-none bg-transparent cursor-pointer z-10 slider-thumb-orange"
                  data-testid="quotatore-cap-slider"
                />
              </div>
              <div className="flex justify-between text-[10px] text-[#4A5E51]">
                {capitaliDisponibili.length > 0 ? (
                  <>
                    <span>{fmt(capitaliDisponibili[0].capitale)}</span>
                    <span>{fmt(capitaliDisponibili[capitaliDisponibili.length - 1].capitale)}</span>
                  </>
                ) : (
                  <span>—</span>
                )}
              </div>
            </div>

            {/* Tagli rapidi */}
            {capitaliDisponibili.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-medium">Premio rapido</p>
                <div className="flex flex-wrap gap-2">
                  {capitaliDisponibili.map((item, i) => (
                    <button
                      key={item.taglio}
                      onClick={() => setCapitaleIdx(i)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        clampedIdx === i
                          ? "bg-[#163321] text-white"
                          : "bg-[#F5F4F0] text-[#4A5E51] hover:bg-[#163321]/10"
                      }`}
                      data-testid={`quotatore-tag-${item.taglio}`}
                    >
                      {fmt(item.taglio)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info + CTA */}
          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-2 p-3 bg-[#F5F4F0] rounded-xl">
              <Info className="w-4 h-4 text-[#4A5E51] mt-0.5 shrink-0" />
              <p className="text-[10px] text-[#4A5E51] leading-relaxed">
                Il capitale assicurato &egrave; determinato esclusivamente dal taglio di premio scelto e dall'et&agrave; dell'Assicurando. Questo &egrave; un preventivo indicativo.
              </p>
            </div>

            <Button
              onClick={() => navigate("/questionario/vita")}
              className="w-full rounded-xl h-12 bg-[#D97B58] text-white hover:bg-[#C06A48] text-sm font-medium"
              data-testid="quotatore-richiedi"
            >
              Procedi con la Richiesta <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* CTA consulenza */}
          <div className="mt-8 p-6 bg-[#F5F4F0] rounded-2xl text-center space-y-3">
            <Shield className="w-8 h-8 text-[#163321] mx-auto" />
            <p className="text-sm text-[#0F1F14] font-medium">Hai bisogno di una consulenza personalizzata?</p>
            <p className="text-xs text-[#4A5E51]">I nostri consulenti sono a tua disposizione per trovare la soluzione migliore per te.</p>
            <Button
              onClick={() => navigate("/questionario/vita")}
              variant="outline"
              className="rounded-full px-6 h-10 border-[#163321] text-[#163321] hover:bg-[#163321]/5 text-sm font-medium"
              data-testid="quotatore-consulenza"
            >
              Richiedi Consulenza Vita
            </Button>
          </div>
        </div>
      </main>
      <Footer />

      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #163321;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          cursor: pointer;
        }
        .slider-thumb::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #163321;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          cursor: pointer;
        }
        .slider-thumb-orange::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #D97B58;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          cursor: pointer;
        }
        .slider-thumb-orange::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #D97B58;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          cursor: pointer;
        }
      `}</style>
    </>
  );
}
